import { t } from "i18next";
import { useCallback, useState } from "react";

import { ApiResponseErrorType } from "~/api/apiResponseTypes.ts";
import { handleApiError } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button/Button.tsx";
import Hint from "~/components/Hint";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay.tsx";
import SidebarContent from "~/components/Sidebar/components/SidebarContent.tsx";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay.tsx";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter.tsx";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader.tsx";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline.tsx";
import type { Invoice } from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";
import { detachInvoiceFromQuota } from "~/modules/quota/api/quota/quotaApiDispatchers.ts";
import type { QuotaWithRelations } from "~/modules/quota/api/quota/quotaTypes.ts";
import {
	getAmountCentsAfterDetach,
	getInvoiceDetachLockMessage,
} from "~/modules/quota/utils/quotaInvoiceUtils.ts";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";

type Props = {
	invoice: Invoice;
	onCancel: () => void;
	onSuccess: () => void;
	quota: QuotaWithRelations;
};

// Detaches one invoice from an invoice-backed quota. The invoice stays. Only its quota link goes.
export const DetachQuotaInvoiceForm = ({ invoice, onCancel, onSuccess, quota }: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const lockMessage = getInvoiceDetachLockMessage(quota, invoice.id);
	const isLastInvoice = quota.invoices.length === 1;
	const invoiceLabel = invoice.invoiceNumber ?? "ohne Rechnungsnummer";

	const onSubmit = useCallback(async () => {
		try {
			setIsBusy(true);
			await detachInvoiceFromQuota(quota.companyId, quota.id, invoice.id);
			onSuccess();
		} catch (error) {
			// The 422 on invoiceId gives the definite answer. A 400 (a booking during the detach) is generic.
			const apiError = handleApiError(error);
			if (apiError.type === ApiResponseErrorType.VALIDATION) {
				setServerErrorMessage(Object.values(apiError.messages)
					.flat()
					.map((detail) => detail.message)
					.join("\n"));
			} else {
				setServerErrorMessage(t("form:unexpectedError"));
			}
			setIsBusy(false);
		}
	}, [invoice.id, onSuccess, quota.companyId, quota.id]);

	return (
		<div className="auto-grid-sidebar">
			<SidebarHeader>
				<SidebarHeaderHeadline>Rechnung {invoiceLabel} entfernen</SidebarHeaderHeadline>
			</SidebarHeader>
			<SidebarContent>
				{isBusy && <SidebarBusyOverlay />}
				{!!serverErrorMessage &&
					<SidebarErrorOverlay title="Entfernen fehlgeschlagen">
						<span className="whitespace-pre-line">{serverErrorMessage}</span>
					</SidebarErrorOverlay>}
				{lockMessage ?
					<Hint theme="error"
						  title="Entfernen nicht möglich">{lockMessage}</Hint> :
					<Hint>
						<p>Die Rechnung bleibt erhalten. Nur ihre Verknüpfung mit dem Kontingent wird entfernt.</p>
						{isLastInvoice ?
							<p>Das Kontingent hat danach keine Rechnung mehr und ist nicht mehr nutzbar.</p> :
							<p>Neues Budget: {formatCentsToCurrency(getAmountCentsAfterDetach(quota, invoice.id))}
								{" "}(bisher {formatCentsToCurrency(quota.amountCents ?? 0)}).</p>}
					</Hint>}
			</SidebarContent>
			<SidebarFooter>
				<Button theme="danger"
						disabled={!!lockMessage || isBusy}
						onClick={onSubmit}>
					Rechnung entfernen
				</Button>
				<Button theme="white"
						onClick={onCancel}>
					{t("form:buttonTexts.cancel")}
				</Button>
			</SidebarFooter>
		</div>);
};
